import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Icons } from '@monorepo/icons';
import {
  Card,
  Flex,
  ProgressSlider,
  Statistic,
  TextComponent,
} from '@monorepo/react-components';
import { useResponsiveness } from '@monorepo/react-hooks';
import { AddOnType, ThemeColors } from '@monorepo/types';
import { IconsNames } from '@monorepo/types';
import AddOns from '../../../add-ons/add-ons';
import DashboardTooltip from '../../dashboard-common/dashboard-tooltip/dashboard-tooltip';

import '../progress-cards-5ers/progress-cards.scss';

interface Props {
  accountId: string;
  allowedDailyLosses: number;
  dailyPnL: number;
}

const DailyPause: FunctionComponent<Props> = ({
  accountId,
  allowedDailyLosses,
  dailyPnL,
}) => {
  const { isMobile } = useResponsiveness();
  const { t } = useTranslation(['dashboardTemplates', 'common']);

  const reachedDailyLoss = dailyPnL * -1 >= allowedDailyLosses;
  const tooltip = t('dashboardTemplates:maximum_dollar_risk_per_day_tooltip');

  let lossLeft = 0;

  if (!reachedDailyLoss) {
    if (dailyPnL >= 0) {
      lossLeft = allowedDailyLosses;
    } else {
      lossLeft = allowedDailyLosses + dailyPnL;
    }
  }

  return (
    <Card
      hoverable={false}
      className={classNames(
        'progress-cards',
        'progress-cards__card',
        'progress-cards__card--daily-pause',
      )}
    >
      <Flex vertical gap={isMobile ? 3 : 0}>
        <Flex justify={'space-between'}>
          <TextComponent weight={400} level={isMobile ? 5 : 3}>
            {t('dashboardTemplates:daily_pause')}
          </TextComponent>
          <DashboardTooltip tooltipText={tooltip} />
        </Flex>

        <Flex justify={'space-between'} align={'center'}>
          <Flex align="baseline" gap={isMobile ? 4 : 8} wrap="wrap">
            <TextComponent level={isMobile ? 3 : 2} weight={600}>
              <Statistic prefix={'$'} value={lossLeft} precision={0} />
            </TextComponent>

            <TextComponent weight={400} level={isMobile ? 3 : 2}>
              {t('common:left')}
            </TextComponent>
          </Flex>
          <AddOns
            accountId={accountId}
            type={AddOnType.BypassDailyPause}
            themeColor={ThemeColors.colorInfo}
            icon={<Icons iconName={IconsNames.Refresh} />}
            displayName={t('common:bypass', { defaultValue: 'Bypass' })}
          />
        </Flex>

        <Flex vertical>
          <ProgressSlider
            disabled
            value={lossLeft}
            min={0}
            max={allowedDailyLosses}
            primaryHandle={false}
            untrackColor={ThemeColors.colorPrimary}
            tooltip={{
              formatter: (value) => (
                <Statistic prefix={'$'} value={value} precision={0} />
              ),
            }}
          />

          <Flex justify="space-between">
            <Flex vertical>
              <TextComponent level={8} weight={600}>
                <Statistic prefix={'$'} value={0} precision={2} />
              </TextComponent>
              <TextComponent level={8}>
                {t('dashboardTemplates:daily_pause')}
              </TextComponent>
            </Flex>

            <Flex vertical align="end">
              <TextComponent level={8} weight={600}>
                <Statistic
                  prefix={'$'}
                  value={allowedDailyLosses}
                  precision={2}
                />
              </TextComponent>
              <TextComponent level={8}>
                {t('dashboardTemplates:daily_loss_allowance')}
              </TextComponent>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};
export default DailyPause;
